import { useEffect, useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { shallow } from 'zustand/shallow';
import { Dialog } from '@components/dialog/Dialog';
import { useStore } from '@store/GlobalStore';
import { UserRoles, internalRoles } from '@models/user';
import { useForm } from 'react-hook-form';
import { Form, FormSection } from '@components/forms';
import { FormFieldType, TFormField } from '@models/forms';
import { Alert } from '@components/alert/Alert';
import { AlertType } from '@pages/PortalAdmin/GlobalAlertManagement/GlobalAlerts.constants';

interface EditRolesModalProps {
  open: boolean;
  onClose: () => void;
}
export const EditRolesModal = ({ open, onClose }: EditRolesModalProps) => {
  const [roles, userAuth, setUserRoles, impersonation] = useStore(
    (state) => [state.roles, state.userAuth, state.setUserRoles, state.impersonation],
    shallow,
  );

  const original = useMemo(() => {
    return impersonation?.userOriginalAuthState ?? { roles, userAuth };
  }, [impersonation, roles, userAuth]);

  type FormValues = {
    isAdmin: boolean;
    isPortalAdmin: boolean;
    isUnderwriter: boolean;
    isLIOContractor: boolean;
    isBroker: boolean;
    isJrOps: boolean;
    isSrOps: boolean;
    isLioSales: boolean;
    isCSR: boolean;
    isCustomerUser: boolean;
  };

  const buildRolesFromAuth = (auth: Partial<FormValues>) => {
    const next: string[] = [];
    if (auth.isAdmin) next.push(UserRoles.SystemAdmin);
    if (auth.isPortalAdmin) next.push(UserRoles.PortalAdmin);
    if (auth.isUnderwriter) next.push(UserRoles.Underwriter);
    if (auth.isLIOContractor) next.push(UserRoles.LIOContractor);
    if (auth.isJrOps) next.push(UserRoles.JrOps);
    if (auth.isSrOps) next.push(UserRoles.SrOps);
    if (auth.isLioSales) next.push(UserRoles.LioSales);
    if (auth.isCSR) next.push(UserRoles.CSR);
    if (auth.isBroker) next.push(UserRoles.Producer);
    if (auth.isCustomerUser) next.push(UserRoles.Quoter);
    return next;
  };

  const buildAuthFromRoles = (rolesIn: string[]) => {
    const nextAuth = {
      ...userAuth,
      isAdmin: rolesIn.includes(UserRoles.SystemAdmin),
      isPortalAdmin: rolesIn.includes(UserRoles.PortalAdmin),
      isUnderwriter: rolesIn.includes(UserRoles.Underwriter),
      isLIOContractor: rolesIn.includes(UserRoles.LIOContractor),
      isBroker: rolesIn.includes(UserRoles.Producer),
      isJrOps: rolesIn.includes(UserRoles.JrOps),
      isSrOps: rolesIn.includes(UserRoles.SrOps),
      isLioSales: rolesIn.includes(UserRoles.LioSales),
      isCSR: rolesIn.includes(UserRoles.CSR),
      isCustomerUser: rolesIn.includes(UserRoles.Quoter),
    } as typeof userAuth;

    const derivedInternal = rolesIn.some((r) => (internalRoles as readonly string[]).includes(r));
    nextAuth.isInternalUser = derivedInternal;
    nextAuth.isInternalWithoutSales = derivedInternal && !rolesIn.includes(UserRoles.LioSales);

    return nextAuth;
  };

  const methods = useForm<FormValues>({
    defaultValues: buildAuthFromRoles(roles),
    mode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      const derived = buildAuthFromRoles(roles);
      methods.reset({
        isAdmin: derived.isAdmin,
        isPortalAdmin: derived.isPortalAdmin,
        isUnderwriter: derived.isUnderwriter,
        isLIOContractor: derived.isLIOContractor,
        isBroker: derived.isBroker,
        isJrOps: derived.isJrOps,
        isSrOps: derived.isSrOps,
        isLioSales: derived.isLioSales,
        isCSR: derived.isCSR,
        isCustomerUser: derived.isCustomerUser,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roles]);

  const onSubmit = (data: FormValues) => {
    const nextRoles = buildRolesFromAuth(data);
    const nextAuth = buildAuthFromRoles(nextRoles);
    setUserRoles({
      roles: nextRoles,
      userAuth: { ...nextAuth, isAuthenticated: true, isLoading: false },
      impersonation: { userOriginalAuthState: original, viewingAs: 'Custom' },
    });
    onClose();
  };

  const handleReset = () => {
    setUserRoles({
      roles: original.roles,
      userAuth: original.userAuth,
      impersonation: undefined,
    });
    onClose();
  };

  const fields: { [fieldName: string]: TFormField } = {
    divider1: {
      name: 'divider1',
      fieldType: FormFieldType.CustomField,
      label: '',
      gridWidth: { xs: 12 },
      fieldConfig: {
        customField: {
          Component: () => <Divider sx={{ width: '100%', my: 0 }} />,
        },
      },
    },
    isAdmin: {
      name: 'isAdmin',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Admin (SystemAdmin)',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isAdmin'),
    },
    isPortalAdmin: {
      name: 'isPortalAdmin',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Portal Admin',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isPortalAdmin'),
    },
    isUnderwriter: {
      name: 'isUnderwriter',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Underwriter',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isUnderwriter'),
    },
    isLIOContractor: {
      name: 'isLIOContractor',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'LIO Contractor',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isLIOContractor'),
    },
    isJrOps: {
      name: 'isJrOps',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Jr Ops',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isJrOps'),
    },
    isSrOps: {
      name: 'isSrOps',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Sr Ops',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isSrOps'),
    },
    isLioSales: {
      name: 'isLioSales',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Lio Sales',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isLioSales'),
    },
    divider2: {
      name: 'divider2',
      fieldType: FormFieldType.CustomField,
      label: '',
      gridWidth: { xs: 12 },
      fieldConfig: {
        customField: {
          Component: () => <Divider sx={{ width: '100%', my: 1 }} />,
        },
      },
    },
    isBroker: {
      name: 'isBroker',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Broker (Producer)',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isBroker'),
    },
    isCSR: {
      name: 'isCSR',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'CSR',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isCSR'),
    },
    isCustomerUser: {
      name: 'isCustomerUser',
      fieldType: FormFieldType.ToggleSwitchField,
      label: 'Customer User (Quoter)',
      gridWidth: { xs: 12 },
      defaultValue: methods.getValues('isCustomerUser'),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title='Edit Roles'
      primaryBtnText='Apply'
      secondaryBtnText='Cancel'
      tertiaryBtnText='Reset to my profile'
      primaryBtnAction={methods.handleSubmit(onSubmit)}
      secondaryBtnAction={onClose}
      tertiaryBtnAction={handleReset}
      size='small'
    >
      <Box>
        <Alert id='update-now-alert' type={AlertType.Info}>
          Refreshing your browser will reset to your original roles.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 3, py: 1 }}>
          <Typography variant='caption' sx={{ color: 'text.subdued' }}>
            Your roles will not actually be updated, you will be impersonating the roles toggles below.
          </Typography>
          <Form methods={methods} formId='edit-roles-form'>
            <FormSection fields={fields} styleConfig={{ spacing: 0.5, marginBottom: 0, marginTop: 0 }} />
          </Form>
        </Box>
        <Alert id='update-now-alert' type={AlertType.Incident}>
          Any bug you encounter please verify with a user with the actual role before logging, this only changes the UI
          for you, it does not affect your API access.
        </Alert>
      </Box>
    </Dialog>
  );
};
